import { Suspense, lazy, ElementType } from 'react';
import { Navigate, useRoutes, useLocation, Outlet } from 'react-router-dom';
// layouts
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
// components
import LoadingScreen from '../components/LoadingScreen';
import MainLayout from 'src/layouts/main';

// ----------------------------------------------------------------------

const Loadable = (Component: ElementType) => (props: any) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  return (
    <Suspense fallback={<LoadingScreen isDashboard={pathname.includes('/app')} />}>
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  return useRoutes([
    {
      element: (
        <MainLayout>
          <Suspense fallback={<LoadingScreen />}>
            <Outlet />
          </Suspense>
        </MainLayout>
      ),
      children: [
        {
          element: <Home />,
          index: true,
        },
        {
          path: 'about',
          element: <About />,
        },
      ],
    },
    {
      path: '/app',
      element: <Dashboard />,
      children: [
        { element: <Navigate to="/app/oracles" />, index: true },
        {
          path: 'oracles',
          children: [
            { element: <Navigate to="/app/oracles/arbitrumOne" />, index: true },
            {
              path: ':networkName/:oracleAddress/:assetAddress',
              element: <OracleAssetView />,
            },
            {
              path: ':networkName/:oracleAddress',
              element: <OracleView />,
            },
            {
              path: ':networkName',
              element: <DataFeeds />,
            },
          ],
        },
        {
          path: 'controllers',
          children: [
            {
              path: ':networkName/:oracleAddress/:assetAddress',
              element: <ControllerAssetView />,
            },
            {
              path: ':networkName/:oracleAddress',
              element: <ControllerView />,
            },
            {
              path: ':networkName',
              element: <ControllerFeeds />,
            },
          ],
        },
        {
          path: 'automatos',
          element: process.env.REACT_APP_SHOW_AUTOMATOS === 'true' ? undefined : <ComingSoon />,
          children:
            process.env.REACT_APP_SHOW_AUTOMATOS === 'true'
              ? [
                  {
                    path: ':networkName/registry/:registryAddress',
                    children: [
                      {
                        element: <AutomatosRegistry />,
                        index: true,
                      },
                      {
                        path: 'pool/:poolAddress',
                        element: <AutomatosPool />,
                      },
                    ],
                  },
                  {
                    path: ':networkName/pool/:poolAddress',
                    element: <AutomatosPoolRegistryResolver />,
                  },
                  {
                    path: ':networkName/factory/:factoryAddress',
                    element: <AutomatosFactory />,
                  },
                  {
                    path: ':networkName',
                    element: <AutomatosRegistries />,
                  },
                ]
              : [
                  {
                    path: '*',
                    element: <ComingSoon />,
                  },
                ],
        },
        {
          path: 'updaters',
          children: [
            {
              path: ':networkName',
              element: <Updaters />,
            },
          ],
        },
      ],
    },
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: '404', element: <NotFound /> },
        { path: '*', element: <Navigate to="/404" /> },
      ],
    },
    { path: '*', element: <Navigate to="/404" /> },
  ]);
}

const Home = Loadable(lazy(() => import('../pages/Home')));
const About = Loadable(lazy(() => import('../pages/About')));
const Dashboard = Loadable(lazy(() => import('../layouts/dashboard')));

// Error pages
const NotFound = Loadable(lazy(() => import('../pages/Page404')));

// Workers
const Updaters = Loadable(lazy(() => import('../pages/Updaters')));

// Oracles
const OracleAssetView = Loadable(lazy(() => import('../pages/OracleAssetView')));
const OracleView = Loadable(lazy(() => import('../pages/OracleView')));
const DataFeeds = Loadable(lazy(() => import('../pages/DataFeeds')));

// Controllers
const ControllerView = Loadable(lazy(() => import('../pages/controller-view')));
const ControllerFeeds = Loadable(lazy(() => import('../pages/controller-feeds')));
const ControllerAssetView = Loadable(lazy(() => import('../pages/controller-asset-view')));

// Automatos
const AutomatosRegistries = Loadable(lazy(() => import('../pages/automatos-registries')));
const AutomatosFactory = Loadable(lazy(() => import('../pages/automatos-factory')));
const AutomatosRegistry = Loadable(lazy(() => import('../pages/automatos-registry')));
const AutomatosPool = Loadable(lazy(() => import('../pages/automatos-pool')));
const AutomatosPoolRegistryResolver = Loadable(
  lazy(() => import('../pages/automatos-pool-registry-resolver'))
);

const ComingSoon = Loadable(lazy(() => import('../pages/coming-soon')));
